'use client';

import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0'),
);

export const MINUTE_OPTIONS = ['00', '15', '30', '45'] as const;

const ITEM_HEIGHT_PX = 34; // h-8 (32px) + gap-0.5 (2px)

export interface TimeUnitInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly string[] | string[];
  maxValue: number;
  placeholder: string;
  ariaLabel: string;
  /** Renders the field read-only: no typing and no dropdown. */
  disabled?: boolean;
}

export function TimeUnitInput({
  id,
  value,
  onChange,
  options,
  maxValue,
  placeholder,
  ariaLabel,
  disabled = false,
}: TimeUnitInputProps) {
  const [open, setOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // The field is usually full (two digits under maxLength=2), so a typed digit
  // is dropped unless the current value is selected. Selecting on click too,
  // because clicking an already-focused field fires no focus event.
  const selectAll = (e: React.SyntheticEvent<HTMLInputElement>) =>
    e.currentTarget.select();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (digits === '') {
      onChange('');
      return;
    }
    const num = Number(digits);
    if (num > maxValue) return;
    onChange(digits.length === 1 ? digits : String(num).padStart(2, '0'));
  };

  const handleSelect = (option: string) => {
    onChange(option);
    setOpen(false);
  };

  useEffect(() => {
    if (!open || !value) return;
    const index = options.indexOf(value);
    if (index < 0) return;
    const timer = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const containerHeight = container.clientHeight;
      const targetScroll = index * ITEM_HEIGHT_PX - containerHeight / 2 + ITEM_HEIGHT_PX / 2;
      container.scrollTop = Math.max(0, Math.min(targetScroll, container.scrollHeight - containerHeight));
    }, 50);
    return () => clearTimeout(timer);
  }, [open, value, options]);

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          className={cn(
            'flex h-9 w-24 items-center rounded-md border border-input bg-background shadow-sm',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          )}
        >
          <input
            id={id}
            type="text"
            inputMode="numeric"
            maxLength={2}
            value={value}
            onChange={handleInputChange}
            disabled={disabled}
            placeholder={placeholder}
            className="h-full w-12 flex-1 border-0 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
            aria-label={ariaLabel}
            onFocus={selectAll}
            onClick={(e) => {
              e.stopPropagation();
              selectAll(e);
            }}
            // Once the field has been clicked with the list open, Radix ignores
            // focus moving elsewhere (a Safari workaround), so tabbing away would
            // leave the list floating over the next field.
            onKeyDown={(e) => {
              if (e.key === 'Tab') setOpen(false);
            }}
          />
          <span
            aria-hidden
            className="flex h-8 w-8 shrink-0 items-center justify-center"
          >
            <ChevronDown className="h-4 w-4" />
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-1"
        align="start"
        // Radix would move focus to the first option ("00"), where typing does
        // nothing and Enter picks that option. Focus isn't moved to the field
        // either: on touch screens that would raise the on-screen keyboard over
        // the list the user opened to tap on.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div
          ref={scrollContainerRef}
          className="max-h-40 overflow-y-auto overflow-x-hidden overscroll-contain"
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-0.5">
            {options.map((option) => (
              <Button
                key={option}
                type="button"
                variant={value === option ? 'default' : 'ghost'}
                size="sm"
                className="h-8 w-full shrink-0 justify-center"
                onClick={() => handleSelect(option)}
              >
                {option}
              </Button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
